import { createSelector } from '@reduxjs/toolkit';

import { SCENARIO_STEP } from 'commonTypes/api/scenarios/BaseTypes';
import type {
  CreditSelectionSection,
  LeadgenGroupSection,
  MPLOffer,
} from 'commonTypes/api/scenarios/CreditSelectionScreenContent';
import type { ContentOfType, StepOfType } from 'commonTypes/api/scenarios/scenario';
import { getParticularStepContent } from 'commonTypes/api/scenarios/scenario';

import { isMplPreApprovalOfferElement } from 'entities/scenario/lib';

import { getScenario, getScenarioContent } from './scenario.selectors';

export const selectCreditSelectionSections = createSelector(getScenarioContent, (content) => {
  return (content as ContentOfType<SCENARIO_STEP.CREDIT_SELECTION>)?.sections || [];
});

export const selectSectionSelectorByType = <TType extends CreditSelectionSection['type']>(sectionType: TType) =>
  createSelector(
    selectCreditSelectionSections,
    (sections): Extract<CreditSelectionSection, { type: TType }> | undefined => {
      return sections?.find(
        (section): section is Extract<CreditSelectionSection, { type: TType }> => section.type === sectionType,
      );
    },
  );

export const selectLeadgenGroup = selectSectionSelectorByType('leadgenGroup');
export const selectOnlineGroup = selectSectionSelectorByType('onlineGroup');

const hasLeadgenGroupElementById = (leadgenGroup: LeadgenGroupSection | undefined, id: string) => {
  if (!leadgenGroup) {
    return false;
  }

  return leadgenGroup.elements.some((element) => element && element.id === id);
};

export const selectHasLeadgenGroup = createSelector(selectLeadgenGroup, (leadgenGroup) => {
  return Boolean(leadgenGroup?.elements?.length);
});

export const selectHasLeadgenSuitableAll = createSelector(selectLeadgenGroup, (leadgenGroup) => {
  return hasLeadgenGroupElementById(leadgenGroup, 'SuitableAll');
});

export const selectHasLeadgenAwaitingCall = createSelector(selectLeadgenGroup, (leadgenGroup) => {
  return hasLeadgenGroupElementById(leadgenGroup, 'AwaitingCall');
});

export const getCreditSelectionFilter = createSelector(getScenario, (step) => {
  return getParticularStepContent(step, SCENARIO_STEP.CREDIT_SELECTION)?.filter;
});

export const selectPlatformConsentModal = createSelector(getScenario, (step) => {
  return getParticularStepContent(step, SCENARIO_STEP.CREDIT_SELECTION)?.platformConsentModal;
});
export const getMplPreApprovalOffers = createSelector(selectOnlineGroup, (onlineGroup) => {
  return onlineGroup?.elements.filter(isMplPreApprovalOfferElement) || [];
});

export const getExchangeOfferIdsByStepRules = createSelector(getScenario, (step) => {
  return (
    (step as StepOfType<SCENARIO_STEP.CREDIT_SELECTION>)?.rules?.find(
      (rule) => rule.type === 'exchangePreApprovalOffer',
    )?.data.exchangeOfferIds || []
  );
});

export const getMplOffers = createSelector(selectOnlineGroup, (onlineGroup) => {
  return (onlineGroup?.elements.filter((e) => e.type === 'mplOffer') || []) as MPLOffer[];
});

export const hasMplOffers = createSelector(getMplOffers, (offers) => {
  return offers.length > 0;
});

export const hasApprovedMplOffers = createSelector(getMplOffers, (offers) => {
  return offers?.some((offer) => offer.status === 'received');
});

export const hasAllRefusedMplOffers = createSelector(getMplOffers, (offers) => {
  return offers?.filter((offer) => offer.status === 'refused').length === offers.length;
});

export const hasMplPreApprovalOffers = createSelector(selectOnlineGroup, (onlineGroup) => {
  return onlineGroup?.elements.some(isMplPreApprovalOfferElement);
});

export const hasMplPreapprovalsInProgressSelector = createSelector(getMplPreApprovalOffers, (mplPreApprovalOffers) => {
  return mplPreApprovalOffers?.some((offer) => offer.status === 'pending');
});

export const hasQueuedMplOffersSelector = createSelector(getMplPreApprovalOffers, (mplPreApprovalOffers) => {
  return mplPreApprovalOffers?.some((offer) => offer.apiStatus === 'Queued');
});

export const getOnlineGroupConfigSelector = createSelector(selectOnlineGroup, (onlineGroup) => {
  const hasOnlyPendingOffers = Boolean(
    onlineGroup?.elements?.length &&
      onlineGroup.elements.every(
        (element) =>
          (element.type === 'mplOffer' || element.type === 'preApprovalsOffer' || element.type === 'mfoDeal') &&
          element.status === 'pending',
      ),
  );

  return {
    onlineGroup,
    hasOffers: !!onlineGroup?.elements?.find((offer) =>
      ['mplOffer', 'preApprovalsOffer', 'mfoDeal'].includes(offer.type),
    ),
    // TODO https://sravni-corp.atlassian.net/browse/CF-6057
    // Сделали небольшой костыль, чтобы не дорабатывать бэк и не рефачить логику usePendingGroupPolling
    hasOnlyPendingOffers,
  };
});
